import { isDateValid } from '@naturehouse/nh-essentials/lib/dates/date';
import calendarEvents, { PeriodType } from '../../../events/calendarEvents';
import { formatDate, isBefore } from '../../../util/dateHelper';
import PeriodStore from '../store/PeriodStore';
import CalendarBase from './CalendarBase';
import { SwitchValue } from './CalendarStateSwitch';
import type Month from './month/Month';

export default class AvailabilityCalendar extends CalendarBase {
    readonly #periodStore: typeof PeriodStore = PeriodStore;

    readonly #availabilityStatusMessages: string[] = ['icalConnection', 'channelManagerConnection'];

    readonly #availabilityDisabled: boolean;

    readonly #includePeriods: boolean;

    readonly #shouldOpenBookedDaySamePage: boolean = false;

    #blockedPeriods: BlockedPeriod[] = [];

    #bookedPeriods: BookedPeriod[] = [];

    #configuredPeriods: Period[] = [];

    #selectedPeriod: DateSelection | null = null;

    #dateSelection: DateRange = { start: null, end: null, id: 0 };

    #ready = false;

    #blockMode = true;

    #disabled: boolean;

    set calendarState(value: string) {
        this.setAttribute('calendar-state', value);
    }

    get calendarState(): string {
        return this.getAttribute('calendar-state') || '';
    }

    set disabled(value: boolean) {
        this.#disabled = value;
        this.toggleAttribute('disabled', value);
    }

    get disabled(): boolean {
        return this.#disabled;
    }

    static get observedAttributes(): string[] {
        return ['calendar-state'];
    }

    constructor() {
        super();

        const houseId = this.getAttribute('houseid');
        this.#includePeriods = this.hasAttribute('include-periods');
        this.#shouldOpenBookedDaySamePage = this.hasAttribute('open-booked-day-same-page');
        this.dataset.ready = 'false';

        this.#availabilityDisabled = this.hasAttribute('availability-disabled');
        this.#disabled = this.hasAttribute('disabled');

        if (houseId !== null) {
            this.#periodStore.setHouseId(houseId).then(() => {
                this.#ready = true;
                this.dataset.ready = 'true';
            });
        }
    }

    protected attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
        if (name === 'calendar-state') {
            this.#blockMode = newValue === SwitchValue.AVAILABILITY;
            this.disabled = newValue === SwitchValue.AVAILABILITY && this.#availabilityDisabled;

            this.#handleConnectedStatusMessages(newValue as SwitchValue);
        }
    }

    public connectedCallback(): void {
        super.connectedCallback();

        this.#handleConnectedStatusMessages();
        this.#setEventListeners();
    }

    protected handleDayClick(event: CustomEvent): void {
        if (!this.#ready || this.#disabled) {
            return;
        }

        const { day, dateString }: { day: HTMLElement; month: HTMLElement; dateString: string } =
            event.detail;

        const date: Date = new Date(dateString);
        if (!isDateValid(date)) {
            return;
        }

        if (day.dataset.configured === '1' && !this.#blockMode) {
            this.#handlePeriodDayClick(date);
            return;
        }

        if (day.dataset.blocked === '1' && this.#blockMode) {
            this.#handleBlockedDayClick(date);
            return;
        }

        if (day.dataset.booked === '1' && !day.dataset.lastDay && this.#blockMode) {
            this.#handleBookedDayClick(date);
            return;
        }

        this.#handleAvailableDayClick(date);
        this.#update();
    }

    protected showMoreMonths(amount: number): void {
        const start: Date = new Date(this.end.getFullYear(), this.end.getMonth() + 1, 1);
        const end: Date = new Date(this.end.getFullYear(), this.end.getMonth() + amount + 1, 0);

        if (this.#includePeriods) {
            this.#periodStore.fetchPeriods(start, end);
        }
        this.#periodStore.fetchBlocks(start, end);
        this.#periodStore.fetchBookedPeriods(start, end);

        super.showMoreMonths(amount);
    }

    #handleBlockedDayClick(date: Date): void {
        const blockedPeriod: BlockedPeriod | undefined = this.#periodStore.getBlockByDate(date);
        if (!blockedPeriod) {
            return;
        }

        if (!blockedPeriod.editable) {
            return;
        }

        this.#dateSelection.id = blockedPeriod.id;
        this.#dateSelection.start = new Date(blockedPeriod.start);
        this.#dateSelection.end = new Date(blockedPeriod.end);

        this.#handleSelectedPeriod();

        document.dispatchEvent(
            new CustomEvent(calendarEvents.dateSelected, {
                detail: {
                    type: PeriodType.Block,
                    period: blockedPeriod,
                    blockMode: this.#blockMode
                }
            })
        );
    }

    #handleAvailableDayClick(date: Date): void {
        // If startDate doesn't exist, fill startDate
        if (
            !this.#dateSelection.start ||
            (this.#dateSelection.start && isBefore(date, this.#dateSelection.start))
        ) {
            this.#dateSelection.start = date;
            this.#handleSelectedPeriod();

            return;
        }

        // If startDate exists but endDate doesn't, fill endDate
        if (this.#dateSelection.start && !this.#dateSelection.end) {
            this.#dateSelection.end = date;

            this.#selectedPeriod = {
                start: formatDate(this.#dateSelection.start),
                end: formatDate(this.#dateSelection.end)
            };

            document.dispatchEvent(
                new CustomEvent(calendarEvents.dateSelected, {
                    detail: {
                        type: this.#blockMode ? PeriodType.Block : PeriodType.DateRange,
                        period: this.#selectedPeriod,
                        blockMode: this.#blockMode
                    }
                })
            );

            return;
        }

        // Reset, fill startDate
        if (this.#dateSelection.start && this.#dateSelection.end) {
            this.#dateSelection = {
                start: date,
                end: null
            };

            this.#selectedPeriod = null;
        }
    }

    #handlePeriodDayClick(date: Date): void {
        const period: Period | undefined = this.#periodStore.getPeriodByDate(date);
        if (!period) {
            return;
        }

        this.#dateSelection.id = period.id;
        this.#dateSelection.start = new Date(period.start);
        this.#dateSelection.end = new Date(period.end);
        this.#dateSelection.end.setDate(this.#dateSelection.end.getDate());

        this.#handleSelectedPeriod();

        document.dispatchEvent(
            new CustomEvent(calendarEvents.dateSelected, {
                detail: {
                    type: PeriodType.Period,
                    period: period,
                    blockMode: this.#blockMode
                }
            })
        );
    }

    #handleBookedDayClick(date: Date): void {
        const bookedPeriod: BookedPeriod | undefined =
            this.#periodStore.getBookedPeriodByDate(date);
        if (!bookedPeriod) {
            return;
        }

        if (bookedPeriod.detailUrl === '') {
            return;
        }

        const target = this.#shouldOpenBookedDaySamePage ? '_self' : '_blank';
        window.open(bookedPeriod.detailUrl, target);
    }

    #handleSelectedPeriod(): void {
        if (!this.#dateSelection.start) {
            return;
        }

        const start: string = formatDate(this.#dateSelection.start);
        const end: string = this.#dateSelection.end
            ? formatDate(this.#dateSelection.end)
            : formatDate(this.#dateSelection.start);

        this.#selectedPeriod = { start, end };
        this.#update();
    }

    #handleConnectedStatusMessages(switchValue = SwitchValue.AVAILABILITY): void {
        const statusesContainer = this.querySelector<HTMLElement>('[data-name="headerStatuses"]');

        if (!statusesContainer) return;

        const statusElements = statusesContainer.querySelectorAll<HTMLElement>(
            '[data-role="status-panel"]'
        );

        if (!statusElements) return;

        statusElements.forEach((statusElement) => {
            const { status } = statusElement.dataset;

            if (!status) return;

            switch (switchValue) {
                case SwitchValue.AVAILABILITY:
                    statusElement.hidden = !this.#availabilityStatusMessages.includes(status);
                    break;
                case SwitchValue.CONFIGURATION:
                    statusElement.hidden = true;
                    break;
                default:
                    statusElement.hidden = true;
            }
        });
    }

    #update(): void {
        this.months.forEach((month: Month): void => {
            month.blocked = this.#blockedPeriods;
            month.booked = this.#bookedPeriods;
            month.configured = this.#configuredPeriods;
            month.selected = this.#selectedPeriod ? [this.#selectedPeriod] : [];
        });
    }

    #setEventListeners(): void {
        document.addEventListener(calendarEvents.periodModalClose, () => {
            this.#dateSelection = {
                start: null,
                end: null
            };

            this.#selectedPeriod = null;
            this.#update();
        });

        [
            calendarEvents.calendarUpdated,
            calendarEvents.blockAdded,
            calendarEvents.blockAddFailed,
            calendarEvents.blockDeleteFailed,
            calendarEvents.periodAdded,
            calendarEvents.periodAddFailed,
            calendarEvents.periodDeleteFailed,
            calendarEvents.periodUpdated,
            calendarEvents.periodUpdateFailed
        ].forEach((event: string) => {
            document.addEventListener(event, () => {
                this.dataset.ready = 'false';

                if (this.#includePeriods) {
                    this.#configuredPeriods = this.#periodStore.state.periods;
                }

                this.#blockedPeriods = this.#periodStore.state.blockedDates;
                this.#bookedPeriods = this.#periodStore.state.bookedPeriods;
                this.#selectedPeriod = null;

                this.#update();
                this.dataset.ready = 'true';
            });
        });

        this.addEventListener('calendar.block-mode', () => {
            this.#blockMode = !this.#blockMode;
        });

        const moreMonthsButton: HTMLButtonElement | null = this.querySelector('#show-more-months');
        if (moreMonthsButton) {
            moreMonthsButton.addEventListener('click', () => {
                this.showMoreMonths(6);
            });
        }
    }
}

if (!customElements.get('availability-calendar')) {
    customElements.define('availability-calendar', AvailabilityCalendar);
}
